import { hnswSearch } from './build-vector-index.js';
export function buildGraph(occupations, searchDocs, hnsw, vectors) {
    const nodes = {};
    const edges = [];
    const adjacency = {};
    const addNode = (id, type, label) => {
        if (!nodes[id]) {
            nodes[id] = { id, type, label };
            adjacency[id] = [];
        }
    };
    const addEdge = (from, to, relationship) => {
        edges.push({ from, to, relationship });
        if (!adjacency[from])
            adjacency[from] = [];
        adjacency[from].push(to);
    };
    const hierarchyGroups = new Map();
    for (const occ of occupations) {
        const occId = `occ:${occ.code}`;
        addNode(occId, 'occupation', occ.description);
        const mgCode = occ.code.substring(0, 1);
        const smgCode = occ.code.substring(0, 2);
        const mngCode = occ.code.substring(0, 3);
        const ugCode = occ.code.substring(0, 4);
        const mgId = `mg:${mgCode}`;
        const smgId = `smg:${smgCode}`;
        const mngId = `mng:${mngCode}`;
        const ugId = `ug:${ugCode}`;
        addNode(mgId, 'majorGroup', occ.majorGroup);
        addNode(smgId, 'subMajorGroup', occ.subMajorGroup);
        addNode(mngId, 'minorGroup', occ.minorGroup);
        addNode(ugId, 'unitGroup', occ.unitGroup);
        addEdge(occId, ugId, 'PART_OF');
        addEdge(ugId, mngId, 'PART_OF');
        addEdge(mngId, smgId, 'PART_OF');
        addEdge(smgId, mgId, 'PART_OF');
        for (const task of occ.tasks) {
            const taskId = `task:${occ.code}:${task.substring(0, 20)}`;
            addNode(taskId, 'task', task);
            addEdge(occId, taskId, 'HAS_TASK');
        }
        for (const alias of occ.alternateTitles) {
            const aliasId = `alias:${occ.code}:${alias}`;
            addNode(aliasId, 'alias', alias);
            addEdge(occId, aliasId, 'HAS_ALIAS');
        }
        if (!hierarchyGroups.has(ugCode))
            hierarchyGroups.set(ugCode, new Set());
        hierarchyGroups.get(ugCode).add(occId);
    }
    for (const groupIds of hierarchyGroups.values()) {
        const ids = Array.from(groupIds);
        for (let i = 0; i < ids.length; i++) {
            for (let j = i + 1; j < Math.min(i + 5, ids.length); j++) {
                addEdge(ids[i], ids[j], 'RELATED_TO');
                addEdge(ids[j], ids[i], 'RELATED_TO');
            }
        }
    }
    for (const doc of searchDocs) {
        const occId = `occ:${doc.code}`;
        const vec = vectors.get(doc.id);
        if (!vec)
            continue;
        const nearest = hnswSearch(hnsw, vectors, vec, 6);
        for (const neighbor of nearest) {
            const neighborDoc = searchDocs[neighbor.id];
            if (neighborDoc && neighborDoc.code !== doc.code) {
                const neighborId = `occ:${neighborDoc.code}`;
                addEdge(occId, neighborId, 'SIMILAR_TO');
            }
        }
    }
    return { nodes, edges, adjacency };
}
const GRAPH_CACHE = new WeakMap();
export function expandGraph(seedIds, graph, maxHops = 3) {
    // The graph is static, so the expansion for a given seed set is fixed. Cache
    // it per-graph (WeakMap keyed by the graph object) so repeated queries —
    // the dominant real-world case — skip the BFS entirely. Keyed by the sorted
    // seed signature so equivalent seed sets share an entry.
    const key = seedIds.slice().sort().join(' ');
    let graphCache = GRAPH_CACHE.get(graph);
    if (!graphCache) {
        graphCache = new Map();
        GRAPH_CACHE.set(graph, graphCache);
    }
    const cached = graphCache.get(key);
    if (cached)
        return cached;
    if (graphCache.size >= 2048) {
        const oldest = graphCache.keys().next().value;
        if (oldest !== undefined)
            graphCache.delete(oldest);
    }
    const scores = bfsExpand(seedIds, graph, maxHops);
    graphCache.set(key, scores);
    return scores;
}
function bfsExpand(seedIds, graph, maxHops) {
    const scores = new Map();
    const hopWeights = [1.0, 0.8, 0.5, 0.3];
    const queue = seedIds.map((id) => ({ id, hop: 0 }));
    let head = 0;
    while (head < queue.length) {
        const { id, hop } = queue[head++];
        if (hop > maxHops)
            continue;
        const weight = hopWeights[hop] ?? 0.1;
        const existing = scores.get(id) ?? 0;
        scores.set(id, Math.min(1.0, existing + weight));
        if (hop < maxHops) {
            const neighbors = graph.adjacency[id] ?? [];
            for (const neighborId of neighbors) {
                if (!scores.has(neighborId) || (scores.get(neighborId) ?? 0) < weight * 0.8) {
                    queue.push({ id: neighborId, hop: hop + 1 });
                }
            }
        }
    }
    return scores;
}
//# sourceMappingURL=build-graph.js.map